/**
 * Banco de ítems del test de nivel (96 ítems) + sembrado inicial.
 *
 * ⚠️ Portado del prototipo `docs/Plan test de nivel/test-nivel-completo-v2.jsx`.
 * No modificar el orden ni los offsets de dificultad (`d`): el resultado del test
 * depende de ellos. Lo añadido respecto al prototipo es un `id` en los ítems de
 * listening (para asociar su audio TTS) y el campo `audioUrl`; ninguno interviene
 * en el cálculo del nivel.
 *
 * Los `script` y `opts` de varios ítems de listening se revisaron para reforzar
 * los distractores (que no se acierten cazando una palabra) y pasar los diálogos
 * a una sola voz. Los offsets `d` y la opción correcta se mantuvieron intactos, así
 * que el cálculo del nivel no cambia. Si cambias un `script`, sincronízalo en
 * `backend-workers/scripts/generate-leveltest-audio.ts` y regenera ese audio.
 *
 * Banco: 66 de texto (Gramática/Vocabulario, 11 por nivel) + 30 de listening
 * (5 por nivel). Competencia = L (Listening) / G (Gramática) / V (Vocabulario).
 * `opts` = lista de [texto, esCorrecta(1|0)]. `d` = dificultad fina; listening +0.3.
 */

import { LEVEL_TEST_AUDIO_URLS } from './levelTestAudio';

export type Skill = 'L' | 'G' | 'V';
export type LevelTestOption = [string, number];

export interface LevelTestItem {
  lvl: number;
  skill: Skill;
  audio: boolean;
  q: string;
  opts: LevelTestOption[];
  d: number;
  /** Sólo en ítems de listening: texto que dice la voz (no se muestra en pantalla). */
  script?: string;
  /** Sólo en ítems de listening: identificador estable para asociar el audio. */
  id?: string;
  /** Sólo en ítems de listening: URL del audio TTS (null si aún no generado). */
  audioUrl?: string | null;
}

// helpers: o = desplazamiento de dificultad dentro del nivel; listening lleva +0.3
const t = (lvl: number, skill: Skill, q: string, opts: LevelTestOption[], o = 0): LevelTestItem => ({
  lvl,
  skill,
  audio: false,
  q,
  opts,
  d: lvl + o,
});

const a = (
  id: string,
  lvl: number,
  script: string,
  q: string,
  opts: LevelTestOption[],
  o = 0
): LevelTestItem => ({
  id,
  lvl,
  skill: 'L',
  audio: true,
  script,
  q,
  opts,
  d: lvl + 0.3 + o,
  audioUrl: LEVEL_TEST_AUDIO_URLS[id] ?? null,
});

export const BANK: LevelTestItem[] = [
  /* TEXTO · A1 */
  t(1, 'G', 'She ___ a teacher.', [['is', 1], ['are', 0], ['am', 0]], -0.3),
  t(1, 'G', '___ are you from?', [['Where', 1], ['What', 0], ['Who', 0]], -0.2),
  t(1, 'G', 'I have two ___.', [['children', 1], ['childs', 0], ['childrens', 0]], 0.1),
  t(1, 'G', 'We ___ TV every evening.', [['watch', 1], ['watches', 0], ['watching', 0]], -0.2),
  t(1, 'G', 'This is ___ apple.', [['an', 1], ['a', 0], ['the', 0]]),
  t(1, 'G', '___ you like coffee?', [['Do', 1], ['Are', 0], ['Does', 0]], -0.1),
  t(1, 'G', 'He goes to work ___ bus.', [['by', 1], ['in', 0], ['on', 0]], 0.1),
  t(1, 'G', 'There ___ a book on the table.', [['is', 1], ['are', 0], ['am', 0]], -0.2),
  t(1, 'V', 'My favourite ___ is blue.', [['colour', 1], ['number', 0], ['day', 0]], -0.2),
  t(1, 'V', "It's cold today. Put on your ___.", [['coat', 1], ['cup', 0], ['clock', 0]], -0.2),
  t(1, 'G', 'They ___ happy.', [['are', 1], ['is', 0], ['am', 0]], -0.3),
  /* TEXTO · A2 */
  t(2, 'G', 'Yesterday I ___ to the cinema.', [['went', 1], ['go', 0], ['gone', 0]]),
  t(2, 'G', 'She is ___ than her sister.', [['taller', 1], ['tall', 0], ['tallest', 0]], -0.1),
  t(2, 'G', "I'm ___ to visit my grandmother tomorrow.", [['going', 1], ['go', 0], ['went', 0]]),
  t(2, 'G', 'They ___ football right now.', [['are playing', 1], ['play', 0], ['plays', 0]]),
  t(2, 'G', "There aren't ___ apples left.", [['any', 1], ['some', 0], ['no', 0]], 0.1),
  t(2, 'G', 'Would you like ___ tea?', [['some', 1], ['any', 0], ['a', 0]]),
  t(2, 'G', 'You ___ finish your homework before dinner.', [['must', 1], ['musts', 0], ['are must', 0]]),
  t(2, 'G', "We didn't ___ to the party last night.", [['go', 1], ['went', 0], ['going', 0]]),
  t(2, 'V', "She works in a hospital. She's a ___.", [['nurse', 1], ['driver', 0], ['teacher', 0]], -0.1),
  t(2, 'V', "I'm thirsty. Can I have a glass of ___?", [['water', 1], ['bread', 0], ['paper', 0]], -0.2),
  t(2, 'G', '___ you go out last night?', [['Did', 1], ['Do', 0], ['Was', 0]]),
  /* TEXTO · B1 */
  t(3, 'G', 'I have ___ been to Japan.', [['never', 1], ['ever', 0], ['yet', 0]]),
  t(3, 'G', 'If it rains, we ___ at home.', [['will stay', 1], ['stay', 0], ['stayed', 0]], -0.1),
  t(3, 'G', 'She ___ smoke, but she quit last year.', [['used to', 1], ['use to', 0], ['is used to', 0]]),
  t(3, 'G', 'The man ___ car was stolen called the police.', [['whose', 1], ['who', 0], ['which', 0]], 0.2),
  t(3, 'G', 'I look forward to ___ from you.', [['hearing', 1], ['hear', 0], ['heard', 0]], 0.1),
  t(3, 'G', 'He has lived here ___ 2010.', [['since', 1], ['for', 0], ['from', 0]]),
  t(3, 'G', 'He suggested ___ to the beach.', [['going', 1], ['to go', 0], ['go', 0]], 0.1),
  t(3, 'G', "I'm not used to ___ up early.", [['getting', 1], ['get', 0], ['got', 0]], 0.2),
  t(3, 'V', "I can't ___ the difference between them.", [['tell', 1], ['say', 0], ['speak', 0]]),
  t(3, 'V', "She's really ___ in photography.", [['interested', 1], ['interesting', 0], ['interest', 0]], -0.1),
  t(3, 'G', "I'd rather have tea ___ coffee.", [['than', 1], ['that', 0], ['then', 0]]),
  /* TEXTO · B2 */
  t(4, 'G', 'The bridge ___ built in 1920.', [['was', 1], ['has', 0], ['is being', 0]], -0.1),
  t(4, 'G', 'She said she ___ tired.', [['was', 1], ['is', 0], ['has been', 0]]),
  t(4, 'G', 'If I ___ you, I would apologize.', [['were', 1], ['was', 0], ['am', 0]]),
  t(4, 'V', 'The meeting was ___ off due to bad weather.', [['called', 1], ['put', 0], ['taken', 0]]),
  t(4, 'G', "I'd rather you ___ smoke in here.", [["didn't", 1], ["don't", 0], ["won't", 0]], 0.2),
  t(4, 'G', 'By the time we arrived, the film ___ started.', [['had', 1], ['has', 0], ['have', 0]]),
  t(4, 'G', 'By next year, she ___ here for a decade.', [['will have worked', 1], ['will work', 0], ['works', 0]], 0.2),
  t(4, 'G', "He's the kind of person ___ always complains.", [['who', 1], ['which', 0], ['whose', 0]], -0.1),
  t(4, 'V', 'The company decided to ___ the new product next month.', [['launch', 1], ['lunch', 0], ['lounge', 0]]),
  t(4, 'V', "He's very ___; he never wastes money.", [['economical', 1], ['economic', 0], ['economy', 0]], 0.1),
  t(4, 'G', 'Hardly had we sat down ___ the lights went out.', [['when', 1], ['than', 0], ['then', 0]], 0.3),
  /* TEXTO · C1 */
  t(5, 'G', 'Not only ___ late, but he also forgot the documents.', [['was he', 1], ['he was', 0], ['he is', 0]], 0.3),
  t(5, 'G', 'The committee insisted that the report ___ submitted by Friday.', [['be', 1], ['is', 0], ['was', 0]], 0.3),
  t(5, 'V', 'Her argument was ___ flawed, lacking any solid evidence.', [['fundamentally', 1], ['fundamental', 0], ['fund', 0]]),
  t(5, 'G', 'Had I known about the delay, I ___ earlier.', [['would have left', 1], ['would leave', 0], ['had left', 0]], 0.3),
  t(5, 'V', 'He took the criticism ___ his stride.', [['in', 1], ['on', 0], ['at', 0]], 0.1),
  t(5, 'G', 'Seldom ___ such a remarkable performance.', [['have we seen', 1], ['we have seen', 0], ['we see', 0]], 0.3),
  t(5, 'V', 'The new policy is likely to ___ controversy.', [['arouse', 1], ['arise', 0], ['rouse', 0]], 0.2),
  t(5, 'V', 'She has a ___ grasp of quantum mechanics.', [['thorough', 1], ['wide', 0], ['large', 0]]),
  t(5, 'V', 'Their entire plan ___ on careful preparation.', [['hinged', 1], ['hanged', 0], ['leaned', 0]], 0.2),
  t(5, 'V', 'The report glosses ___ the most serious problems.', [['over', 1], ['off', 0], ['out', 0]], 0.1),
  t(5, 'G', 'Only after the meeting ___ the full implications.', [['did we realize', 1], ['we realized', 0], ['we did realize', 0]], 0.3),
  /* TEXTO · C2 */
  t(6, 'V', 'The proposal was met with ___ enthusiasm — nobody objected.', [['unanimous', 1], ['united', 0], ['uniform', 0]]),
  t(6, 'V', 'She has an encyclopaedic ___ of medieval history.', [['knowledge', 1], ['wisdom', 0], ['intelligence', 0]]),
  t(6, 'V', 'He tends to ___ over the details and miss the big picture.', [['pore', 1], ['pour', 0], ['poor', 0]], 0.2),
  t(6, 'V', 'Their objections were, ___ all intents and purposes, irrelevant.', [['for', 1], ['to', 0], ['by', 0]], 0.2),
  t(6, 'V', "The minister's resignation ___ a deeper crisis within the party.", [['portended', 1], ['belied', 0], ['forwent', 0]], 0.3),
  t(6, 'V', "I wouldn't ___ it past him to lie.", [['put', 1], ['place', 0], ['set', 0]], 0.2),
  t(6, 'V', 'Such conduct is wholly ___ with the values of this institution.', [['incompatible', 1], ['incompetent', 0], ['incongruous', 0]]),
  t(6, 'V', 'The novel is a ___ critique of consumerism.', [['scathing', 1], ['scalding', 0], ['scolding', 0]], 0.1),
  t(6, 'V', 'The two accounts of events are completely at ___.', [['odds', 1], ['ends', 0], ['evens', 0]], 0.2),
  t(6, 'V', 'She has a ___ wit that delights audiences.', [['rapier', 1], ['blunt', 0], ['dull', 0]], 0.2),
  t(6, 'V', 'His remarks were a thinly ___ insult.', [['veiled', 1], ['hidden', 0], ['covered', 0]]),
  /* LISTENING · A1 */
  a('L-A1-01', 1, "Hi, I'm Sara. I'm twelve years old.", 'How old is Sara?', [['Twelve', 1], ['Twenty', 0], ['Two', 0]]),
  a('L-A1-02', 1, "The train leaves at nine o'clock.", 'What time does the train leave?', [['9:00', 1], ['5:00', 0], ['10:00', 0]]),
  a('L-A1-03', 1, 'Can you pass me the red book — not the blue one?', 'What does the speaker want?', [['The red book', 1], ['The blue book', 0], ['A pen', 0]]),
  a('L-A1-04', 1, 'My sister has a small black cat and a big white dog.', 'What colour is the cat?', [['Black', 1], ['White', 0], ['Brown', 0]]),
  a('L-A1-05', 1, "Let's meet at the bus stop, not at the station.", 'Where will they meet?', [['At the bus stop', 1], ['At the station', 0], ['At school', 0]]),
  /* LISTENING · A2 */
  a('L-A2-01', 2, "I'm off to the supermarket for some milk — maybe the cinema after.", 'Where is the speaker going first?', [['To the supermarket', 1], ['To the cinema', 0], ['To the chemist', 0]]),
  a('L-A2-02', 2, "I'd normally walk, but in this rain I'll get the bus.", 'How will the speaker get to work today?', [['By bus', 1], ['On foot', 0], ['By car', 0]]),
  a('L-A2-03', 2, "The museum's closed on Mondays, but open Tuesday to Sunday.", 'When is the museum closed?', [['On Mondays', 1], ['At weekends', 0], ['On Tuesdays', 0]]),
  a('L-A2-04', 2, "I've already had breakfast, but I'm still hungry.", 'How does the speaker feel?', [['Still hungry', 1], ['No longer hungry', 0], ['Thirsty', 0]]),
  a('L-A2-05', 2, "Honestly, glorious weather — it didn't rain once all week.", 'What was the weather like?', [['Sunny', 1], ['Rainy', 0], ['Changeable', 0]]),
  /* LISTENING · B1 */
  a('L-B1-01', 3, "Attention, passengers. The 6:15 to Leeds isn't cancelled — just delayed by twenty minutes.", 'What is the problem?', [['The train is late', 1], ['The train is cancelled', 0], ['The train has left', 0]]),
  a('L-B1-02', 3, "I'd love to, and I'm not ill or anything — I just have to study for Friday's exam.", "Why can't the speaker go to the party?", [['She has to study', 1], ['She is ill', 0], ["She wasn't invited", 0]]),
  a('L-B1-03', 3, 'We were going to eat outside, but it got too cold, so we stayed in.', 'Where did they eat?', [['Inside', 1], ['Outside', 0], ['At a restaurant', 0]]),
  a('L-B1-04', 3, "It's not the rain — the shop shuts in five minutes, so we'd better hurry.", 'Why must they hurry?', [['The shop is about to close', 1], ["It's raining", 0], ["They're late for a train", 0]]),
  a('L-B1-05', 3, "I've been learning the guitar for about a year now.", 'How long has the speaker played the guitar?', [['About a year', 1], ['A few weeks', 0], ['Ten years', 0]]),
  /* LISTENING · B2 */
  a('L-B2-01', 4, "Honestly, the film wasn't as bad as everyone said — I actually quite enjoyed it.", "What is the speaker's opinion of the film?", [['Better than expected', 1], ['Terrible', 0], ['Just as bad as people said', 0]]),
  a('L-B2-02', 4, "I'd rather we postponed the meeting than rush a decision we'll regret.", 'What does the speaker prefer?', [['To delay the meeting', 1], ['To decide quickly', 0], ['To cancel the project', 0]]),
  a('L-B2-03', 4, "The report's almost done — I just need to double-check the figures.", 'Is the report finished?', [['Not quite', 1], ['Yes, completely', 0], ["It hasn't been started", 0]]),
  a('L-B2-04', 4, "If I'd known you were coming, I'd have made dinner.", 'What does the speaker imply?', [["They didn't know the listener was coming", 1], ['They already cooked dinner', 0], ["They don't want the visitor", 0]], 0.2),
  a('L-B2-05', 4, "Let's skip the motorway — it's not the tolls, there's been an accident.", 'Why avoid the motorway?', [["There's been an accident", 1], ['It costs too much', 0], ["It's longer", 0]]),
  /* LISTENING · C1 */
  a('L-C1-01', 5, "I'm not saying the proposal is wrong, exactly — just that the timing couldn't be worse.", "What is the speaker's main concern?", [['The timing', 1], ['The cost', 0], ['The author of the proposal', 0]], 0.2),
  a('L-C1-02', 5, "She handled the interview with such composure that you'd never have guessed how nervous she was.", 'What does the speaker imply?', [['She seemed calm despite being nervous', 1], ['She was visibly nervous', 0], ['She refused the interview', 0]], 0.2),
  a('L-C1-03', 5, "Frankly, the numbers speak for themselves; I won't waste your time defending them.", 'What does the speaker mean?', [['The data is convincing on its own', 1], ['The data is unreliable', 0], ['He needs more time to explain', 0]]),
  a('L-C1-04', 5, 'To be fair, she did warn us this might happen.', 'What is the speaker acknowledging?', [['She gave a warning beforehand', 1], ['She caused the problem', 0], ['She said nothing', 0]]),
  a('L-C1-05', 5, 'The launch went ahead, albeit a few weeks later than planned.', 'What happened to the launch?', [['It happened, but late', 1], ['It was cancelled', 0], ['It was early', 0]], 0.1),
  /* LISTENING · C2 */
  a('L-C2-01', 6, "Well, that's one way to put it — though I suspect you're being rather generous.", 'What does the speaker suggest?', [['The description is too kind', 1], ['The description is accurate', 0], ['They completely agree', 0]], 0.2),
  a('L-C2-02', 6, 'For all his bluster, when it came to the crunch he didn\'t lift a finger.', 'What does the speaker say about the man?', [['He talked a lot but did nothing', 1], ['He worked extremely hard', 0], ['He stayed completely silent', 0]], 0.2),
  a('L-C2-03', 6, "I'd take her assurances with a pinch of salt, if I were you.", 'What is the speaker advising?', [['Be sceptical of her promises', 1], ['Trust her completely', 0], ['Repeat her words exactly', 0]], 0.2),
  a('L-C2-04', 6, "He's nothing if not persistent — he's called three times today already.", 'What does the speaker mean?', [['He is very persistent', 1], ['He never calls', 0], ['He has given up', 0]], 0.1),
  a('L-C2-05', 6, "I'll believe that when I see it, frankly.", 'What is the speaker expressing?', [['Doubt', 1], ['Certainty', 0], ['Excitement', 0]]),
];

// Autoevaluación inicial (can-do MCER) → siembra el θ de partida
export interface SeedQuestion {
  q: string;
  opts: [string, number][];
}

export const SEED: SeedQuestion[] = [
  {
    q: 'Cuando te hablan en inglés, ¿cuánto entiendes?',
    opts: [
      ['Solo palabras sueltas', 1.5],
      ['Frases sencillas si hablan despacio', 2.5],
      ['Conversaciones del día a día', 3.5],
      ['Casi todo, aunque hablen rápido', 5.0],
    ],
  },
  {
    q: '¿Sigues una serie o película en inglés sin subtítulos?',
    opts: [
      ['No, casi nada', 2.0],
      ['Con esfuerzo, a ratos', 3.5],
      ['Sí, sin problema', 5.0],
    ],
  },
];

export const CEFR = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
export const SESSION = 26;
export const GUESS = 0.33; // 3 opciones → 1/3 de acierto por azar
export const SKILL_LABEL: Record<Skill, string> = { L: 'Listening', G: 'Gramática', V: 'Vocabulario' };
